import { API_URL } from '../config/constants';
import {
  normalizeEndpointForTelemetry,
  normalizeRouteForTelemetry,
  sanitizeInstrumentationPayload,
} from '../utils/instrumentation';

const FULL_SHA_PATTERN = /^[a-f0-9]{40}$/i;
const ALLOWED_EVENTS = new Set([
  'client_error',
  'web_vital',
  'route_transition',
  'api_authenticated_error',
  'import_status_refresh_failed',
]);
const EVENT_FIELDS = Object.freeze({
  client_error: ['errorType', 'boundary', 'componentStackPresent'],
  web_vital: ['metric', 'value', 'rating', 'target', 'exceedsGoodThreshold', 'delta', 'navigationType'],
  route_transition: ['from', 'to', 'durationMs'],
  api_authenticated_error: ['endpoint', 'method', 'status', 'durationMs', 'retried'],
  import_status_refresh_failed: [
    'phase', 'aiPhase', 'canEnterDashboard', 'reviewReady',
    'historicalImportComplete', 'failed', 'retryAvailable',
  ],
});
const initializedState = { enabled: false, attempted: false, releaseSha: null };
const nativeFetch = (...args) => window.fetch(...args);
let sendRequest = nativeFetch;

const getReleaseSha = () => {
  const value = String(
    process.env.REACT_APP_VYLO_RELEASE_SHA
      || process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA
      || ''
  ).trim();
  return FULL_SHA_PATTERN.test(value) ? value.toLowerCase() : null;
};

const buildTelemetryEnvelope = (eventName, payload = {}) => {
  const normalizedName = String(eventName || '').trim().toLowerCase();
  if (!ALLOWED_EVENTS.has(normalizedName) || !initializedState.releaseSha) return null;

  const safePayload = sanitizeInstrumentationPayload(payload || {});
  const allowedPayload = Object.fromEntries(
    EVENT_FIELDS[normalizedName]
      .filter((key) => safePayload[key] !== undefined && safePayload[key] !== '[redacted]')
      .map((key) => {
        if (key === 'from' || key === 'to') return [key, normalizeRouteForTelemetry(safePayload[key])];
        if (key === 'endpoint') return [key, normalizeEndpointForTelemetry(safePayload[key])];
        return [key, safePayload[key]];
      })
  );
  return {
    eventName: normalizedName,
    releaseSha: initializedState.releaseSha,
    platform: 'web',
    clientVersion: process.env.REACT_APP_VERSION || null,
    clientBuild: null,
    route: normalizeRouteForTelemetry(window.location?.pathname || '/'),
    payload: allowedPayload,
  };
};

const sendEnvelope = (envelope) => {
  if (!envelope) return null;
  return sendRequest(`${API_URL}/telemetry/client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(envelope),
    credentials: 'omit',
    keepalive: true,
  }).catch(() => null);
};

const initializeProductionTelemetry = () => {
  if (initializedState.attempted) return initializedState.enabled;
  initializedState.attempted = true;
  initializedState.releaseSha = getReleaseSha();

  if (process.env.NODE_ENV !== 'production' || !initializedState.releaseSha) return false;

  window.__VYLO_INSTRUMENTATION__ = (eventName, payload) => {
    if (!ALLOWED_EVENTS.has(eventName)) return;
    void sendEnvelope(buildTelemetryEnvelope(eventName, payload));
  };
  initializedState.enabled = true;
  return true;
};

const captureProductionException = (error, context = {}) => {
  if (!initializedState.enabled) return null;
  return sendEnvelope(buildTelemetryEnvelope('client_error', {
    errorType: error?.name || 'Error',
    boundary: context.boundary || 'unknown',
    componentStackPresent: context.componentStackPresent === true,
  }));
};

const resetProductionTelemetryForTests = () => {
  initializedState.enabled = false;
  initializedState.attempted = false;
  initializedState.releaseSha = null;
  sendRequest = nativeFetch;
  delete window.__VYLO_INSTRUMENTATION__;
};

const setTelemetryTransportForTests = (transport) => {
  sendRequest = transport;
};

export {
  buildTelemetryEnvelope,
  captureProductionException,
  getReleaseSha,
  initializeProductionTelemetry,
  resetProductionTelemetryForTests,
  setTelemetryTransportForTests,
};
